import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ModalWrapper } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper } from "@msuite/ui/table";
import { Tooltip } from "@msuite/ui/tooltip";
import type { IncomingInvoiceReminderListItem } from "@msuite/utils/types";
import React from "react";
import { IncomingInvoiceDocumentPreviewButton } from "@/components/incoming-invoices";
import { useCitadel } from "@/hooks/use-citadel";
import { LinkReminderModal } from "@/modals/incoming-invoices/link-reminder-modal";

/**
 * The reminder's PDF in the viewer, and its link to the invoice it reminds of — set by hand for
 * one the match left unlinked, changed for one linked wrongly. A matched reminder also opens its
 * invoice.
 */
export function RowActions({ cell }: CellContext<IncomingInvoiceReminderListItem, "actions">) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { citadel } = useCitadel();
	const linkModal = useDisclosure();

	/** Computed */
	const reminder = cell.row.original;
	const { invoice, fileId } = reminder;
	const mayLink = citadel?.can("update", "IncomingInvoice") === true;

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper to={invoice ? `/controlling/incoming-invoices/${invoice.id}` : null}>
				{fileId && <IncomingInvoiceDocumentPreviewButton fileId={fileId} />}
				{mayLink && (
					<Tooltip label={t("incoming_invoices.reminders.link_invoice")}>
						<Button
							size="xxs"
							variant="ghost"
							icon={<Icon name="Link2" />}
							onClick={linkModal.onOpen}
							data-cy="link-incoming-invoice-reminder-button"
						/>
					</Tooltip>
				)}
			</RowActionsWrapper>
			<ModalWrapper>
				{linkModal.isOpen && (
					<LinkReminderModal
						isOpen={linkModal.isOpen}
						onClose={linkModal.onClose}
						reminder={reminder}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
